/**
 * A sorting function to be used in `Array::sort`.
 */
export type SortFn<T> = (a: T, b: T) => number;

export enum Ordering {
  ASCENDING,
  DESCENDING,
}

export enum NullOrder {
  FIRST,
  LAST,
}

type Sortable = string | number | bigint | boolean | object | null | undefined;
type Accessor<T> = (v: T) => Sortable;

export type Field<T> = {
  /**
   * The sorting order for the given key. Defaults to `DESCENDING`.
   */
  order?: Ordering;
} & (
  | {
      /**
       * The name of the field to sort by.
       */
      name: keyof T;
    }
  | {
      /**
       * A function to access the field to sort by.
       */
      accessor: Accessor<T>;
    }
);

export type SortOptions = {
  /**
   * Determines whether `null` and `undefined` values are put at the start or at the end of the result.
   */
  nulls?: NullOrder;
};

/**
 * Returns a sorting function which can sort an array based on multiple fields.
 *
 * @example
 *  // results in [{ a: 1, b: 1 }, { a: 1, b: 2 }, { a: null, b: 0 }]
 *  [{ a: 1, b: 1 }, { a: null, b: 0 }, { a: 1, b: 2 }]
 *    .sort(sortByKey([
 *      { name: 'a', order: Ordering.DESCENDING },
 *      { name: 'b', order: Ordering.ASCENDING },
 *    ], {
 *      nulls: NullOrder.LAST
 *    }));
 */
export function sortBy<T>(
  fields: Field<T>[],
  options?: SortOptions,
): SortFn<T> {
  const nulls = options?.nulls ?? NullOrder.LAST;
  const accessors = fields.reduce((map, field) => {
    let accessor: Accessor<T>;
    if ('accessor' in field) {
      accessor = field.accessor;
    } else {
      accessor = (v) => v[field.name] as Sortable;
    }
    map.set(field, accessor);
    return map;
  }, new Map<Field<T>, Accessor<T>>());

  return (a, b) => {
    for (const field of fields) {
      const order = field.order ?? Ordering.DESCENDING;
      const accessor = accessors.get(field)!;
      const aValue = accessor(a);
      const bValue = accessor(b);

      if (aValue === undefined || aValue === null) {
        return nulls === NullOrder.FIRST ? -1 : 1;
      } else if (bValue === undefined || bValue === null) {
        return 0;
      }

      if (aValue > bValue) {
        return order === Ordering.ASCENDING ? 1 : -1;
      }

      if (aValue < bValue) {
        return order === Ordering.ASCENDING ? -1 : 1;
      }
    }

    return 0;
  };
}

/**
 * Returns a sorting function which can sort an array based on the indices of a given array.
 *
 * @example
 *  // results in [5, 3, 1, 2, 4]
 *  [1, 2, 3, 4, 5]
 *    .sort(sortBy([
 *      5, 3, 1, 2
 *    ], {
 *      nulls: NullOrder.LAST
 *    }));
 */
export function sortByIndex<T>(values: T[], options?: SortOptions): SortFn<T> {
  const nulls = options?.nulls ?? NullOrder.LAST;

  return (a, b) => {
    const aIndex = values.indexOf(a);
    const bIndex = values.indexOf(b);

    if (aIndex === -1 || bIndex === -1) {
      return nulls === NullOrder.FIRST ? -1 : 1;
    }

    if (aIndex > bIndex) {
      return 1;
    }

    if (aIndex < bIndex) {
      return -1;
    }

    return 0;
  };
}
